import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, limit } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useUser } from '../context/UserContext';

interface CollectionData {
  name: string;
  count: number;
  sample: any[];
  schema?: { [key: string]: string };
}

export default function DatabaseAnalysis() {
  const { user } = useUser();
  const [collections, setCollections] = useState<CollectionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState<any>(null);

  useEffect(() => {
    const analyzeDatabase = async () => {
      try {
        const collectionsToAnalyze = [
          'admin', 'asociado', 'asesor', 'inmobiliaria', 
          'propiedades', 'proyectos', 'taller', 
          'inscripcion', 'calificacion', 'configuracion_rating'
        ];
        
        const results: CollectionData[] = [];
        
        for (const collectionName of collectionsToAnalyze) {
          try {
            const q = query(collection(db, collectionName), limit(5));
            const snapshot = await getDocs(q);
            
            const sample = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            // Analizar esquema
            const schema: { [key: string]: string } = {};
            if (sample.length > 0) {
              Object.keys(sample[0]).forEach(key => {
                const value = (sample[0] as any)[key];
                schema[key] = Array.isArray(value) ? 'array' : typeof value;
              });
            }
            
            results.push({
              name: collectionName,
              count: snapshot.size,
              sample: sample.slice(0, 2), // Solo primeros 2 para UI
              schema
            });
            
          } catch (error) {
            console.error(`Error analyzing ${collectionName}:`, error);
            results.push({
              name: collectionName,
              count: 0,
              sample: [],
              schema: {}
            });
          }
        }
        
        setCollections(results);
        
        // Análisis de problemas
        const problemAnalysis = {
          inconsistentIds: analyzeIdInconsistencies(results),
          duplicateEmails: await analyzeDuplicateEmails(),
          orphanedReferences: analyzeOrphanedReferences(results),
          schemaIssues: analyzeSchemaIssues(results)
        };
        
        setAnalysis(problemAnalysis);
        
      } catch (error) {
        console.error('Error en análisis de base de datos:', error);
      } finally {
        setLoading(false);
      }
    };
    
    analyzeDatabase();
  }, []);

  const analyzeIdInconsistencies = (collections: CollectionData[]) => {
    const issues: string[] = [];
    
    collections.forEach(col => {
      if (col.sample.length > 0) {
        const doc = col.sample[0];
        const hasFirebaseUid = 'firebase_uid' in doc;
        const hasBusinessId = col.name === 'admin' ? 'admin_id' in doc :
                             col.name === 'asociado' ? 'asociado_id' in doc :
                             col.name === 'asesor' ? 'asesor_id' in doc :
                             col.name === 'inmobiliaria' ? 'inmobiliaria_id' in doc : false;
        
        if (!hasFirebaseUid && col.name !== 'inmobiliaria') {
          issues.push(`❌ ${col.name}: Falta firebase_uid`);
        }
        
        if (!hasBusinessId && col.name !== 'propiedades') {
          issues.push(`❌ ${col.name}: Falta business_id`);
        }
      }
    });
    
    return issues;
  };

  const analyzeDuplicateEmails = async () => {
    const issues: string[] = [];
    const emails: string[] = [];
    
    try {
      const userCollections = ['admin', 'asociado', 'asesor'];
      
      for (const colName of userCollections) {
        const snapshot = await getDocs(collection(db, colName));
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.correo) {
            const email = data.correo.toLowerCase();
            if (emails.includes(email)) {
              issues.push(`🔄 Email duplicado: ${email} en ${colName}`);
            } else {
              emails.push(email);
            }
          }
        });
      }
    } catch (error) {
      issues.push('❌ Error analizando emails duplicados');
    }
    
    return issues;
  };

  const analyzeOrphanedReferences = (collections: CollectionData[]) => {
    const issues: string[] = [];
    
    // Por ejemplo: inmobiliaria con asesor_id[] que no existen
    const inmobiliaria = collections.find(c => c.name === 'inmobiliaria');
    if (inmobiliaria && inmobiliaria.sample.length > 0) {
      inmobiliaria.sample.forEach(inmo => {
        if (inmo.asesor_id && Array.isArray(inmo.asesor_id)) {
          if (inmo.asesor_id.length === 0) {
            issues.push(`⚠️ Inmobiliaria ${inmo.nombre}: Sin asesores asignados`);
          }
        }
        if (inmo.asociado_id && Array.isArray(inmo.asociado_id)) {
          if (inmo.asociado_id.length === 0) {
            issues.push(`⚠️ Inmobiliaria ${inmo.nombre}: Sin asociados asignados`);
          }
        }
      });
    }
    
    return issues;
  };

  const analyzeSchemaIssues = (collections: CollectionData[]) => {
    const issues: string[] = [];
    
    collections.forEach(col => {
      if (col.schema) {
        // Revisar tipos inconsistentes
        Object.entries(col.schema).forEach(([field, type]) => {
          if (field.includes('id') && type !== 'string' && type !== 'array') {
            issues.push(`📋 ${col.name}.${field}: Tipo ${type} debería ser string`);
          }
        });
      }
    });
    
    return issues;
  };

  // Solo admins pueden acceder
  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Acceso Denegado</h4>
          <p>Solo administradores pueden ver el análisis de base de datos.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Analizando base de datos...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h3 className="mb-0">🔍 Análisis de Base de Datos Firebase</h3>
              <small>Diagnóstico completo antes de migración AMPI</small>
            </div>
            <div className="card-body">
              
              {/* Resumen de colecciones */}
              <div className="row mb-4">
                {collections.map((col, index) => (
                  <div key={col.name} className="col-md-3 mb-3">
                    <div className="card border-info">
                      <div className="card-header bg-info text-white text-center">
                        <h6 className="mb-0">{col.name}</h6>
                      </div>
                      <div className="card-body text-center">
                        <h4 className={col.count > 0 ? 'text-success' : 'text-warning'}>
                          {col.count}
                        </h4>
                        <small className="text-muted">documentos</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Análisis de problemas */}
              {analysis && (
                <div className="row">
                  <div className="col-md-6">
                    <div className="card border-danger">
                      <div className="card-header bg-danger text-white">
                        <h5 className="mb-0">🚨 Problemas Críticos</h5>
                      </div>
                      <div className="card-body">
                        
                        <h6 className="text-danger">IDs Inconsistentes:</h6>
                        <ul className="list-unstyled">
                          {analysis.inconsistentIds.map((issue: string, i: number) => (
                            <li key={i} className="small text-danger mb-1">{issue}</li>
                          ))}
                        </ul>

                        <h6 className="text-warning">Emails Duplicados:</h6>
                        <ul className="list-unstyled">
                          {analysis.duplicateEmails.map((issue: string, i: number) => (
                            <li key={i} className="small text-warning mb-1">{issue}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="card border-warning">
                      <div className="card-header bg-warning text-white">
                        <h5 className="mb-0">⚠️ Problemas Estructurales</h5>
                      </div>
                      <div className="card-body">
                        
                        <h6 className="text-info">Referencias Huérfanas:</h6>
                        <ul className="list-unstyled">
                          {analysis.orphanedReferences.map((issue: string, i: number) => (
                            <li key={i} className="small text-info mb-1">{issue}</li>
                          ))}
                        </ul>

                        <h6 className="text-secondary">Esquemas:</h6>
                        <ul className="list-unstyled">
                          {analysis.schemaIssues.map((issue: string, i: number) => (
                            <li key={i} className="small text-secondary mb-1">{issue}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Detalles de colecciones */}
              <div className="mt-4">
                <h5>📋 Detalles de Colecciones</h5>
                <div className="accordion" id="collectionsAccordion">
                  {collections.map((col, index) => (
                    <div key={col.name} className="accordion-item">
                      <h2 className="accordion-header">
                        <button 
                          className="accordion-button collapsed" 
                          type="button" 
                          data-bs-toggle="collapse" 
                          data-bs-target={`#collapse${index}`}
                        >
                          {col.name} ({col.count} docs) - Esquema: {Object.keys(col.schema || {}).length} campos
                        </button>
                      </h2>
                      <div id={`collapse${index}`} className="accordion-collapse collapse">
                        <div className="accordion-body">
                          <div className="row">
                            <div className="col-md-6">
                              <h6>📊 Esquema:</h6>
                              <pre className="bg-light p-2 small">
                                {JSON.stringify(col.schema, null, 2)}
                              </pre>
                            </div>
                            <div className="col-md-6">
                              <h6>📄 Ejemplo de datos:</h6>
                              <pre className="bg-light p-2 small" style={{maxHeight: '300px', overflow: 'auto'}}>
                                {JSON.stringify(col.sample[0], null, 2)}
                              </pre>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}